import { useState } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { toast } from "react-toastify";

import { createSponsorCode } from "~/api/users/createSponsorCode";
import { sendSponsorEmail } from "~/api/users/sendSponsorEmail";
import MainButton from "~/components/common/mainButton";
import { useLanguage } from "~/contexts/providers/language";
import englishTranslations from "~/localization/en";
import frenchTranslationsy from "~/localization/fr";
import {
  SponsorshipEmailFields,
  SponsorshipFormFields,
} from "~/types/account/account.types";

import styles from "./index.module.scss";

const AccountSponsorship = () => {
  const { register, handleSubmit, getValues } =
    useForm<SponsorshipFormFields>();
  const [sponsorCode, setSponsorCode] = useState<string>();

  const { language } = useLanguage();

  const intro =
    language === "En"
      ? englishTranslations.accountPage.sponsorship.intro
      : frenchTranslationsy.accountPage.sponsorship.intro;
  const friendInfo =
    language === "En"
      ? englishTranslations.accountPage.sponsorship.friendInfo
      : frenchTranslationsy.accountPage.sponsorship.friendInfo;
  const firstName =
    language === "En"
      ? englishTranslations.accountPage.sponsorship.firstName
      : frenchTranslationsy.accountPage.sponsorship.firstName;
  const lastName =
    language === "En"
      ? englishTranslations.accountPage.sponsorship.lastName
      : frenchTranslationsy.accountPage.sponsorship.lastName;
  const email =
    language === "En"
      ? englishTranslations.accountPage.sponsorship.email
      : frenchTranslationsy.accountPage.sponsorship.email;
  const send =
    language === "En"
      ? englishTranslations.accountPage.sponsorship.send
      : frenchTranslationsy.accountPage.sponsorship.send;
  const sponsorCodeText =
    language === "En"
      ? englishTranslations.accountPage.sponsorship.sponsorCode
      : frenchTranslationsy.accountPage.sponsorship.sponsorCode;
  const sponsorCodeRequest =
    language === "En"
      ? englishTranslations.accountPage.sponsorship.sponsorCodeRequest
      : frenchTranslationsy.accountPage.sponsorship.sponsorCodeRequest;
  const sendByEmail =
    language === "En"
      ? englishTranslations.accountPage.sponsorship.sendByEmail
      : frenchTranslationsy.accountPage.sponsorship.sendByEmail;
  const generateCode =
    language === "En"
      ? englishTranslations.accountPage.sponsorship.generateCode
      : frenchTranslationsy.accountPage.sponsorship.generateCode;
  const sponsorErrorMessage =
    language === "En"
      ? englishTranslations.accountPage.sponsorship.sponsorErrorMessage
      : frenchTranslationsy.accountPage.sponsorship.sponsorErrorMessage;
  const generateSponsorCodeFirst =
    language === "En"
      ? englishTranslations.accountPage.sponsorship.generateSponsorCodeFirst
      : frenchTranslationsy.accountPage.sponsorship.generateSponsorCodeFirst;
  const sponsorCodeSent =
    language === "En"
      ? englishTranslations.accountPage.sponsorship.sponsorCodeSent
      : frenchTranslationsy.accountPage.sponsorship.sponsorCodeSent;

  const { mutate } = useMutation(createSponsorCode, {
    onSuccess: (res) => {
      setSponsorCode(res.code);
    },
    onError: (error) => {
      console.error("ERROR IN SPONSOR:", error);
    },
  });
  const { mutate: emailMutate } = useMutation(sendSponsorEmail, {
    onSuccess: () => {
      toast.success(sponsorCodeSent);
    },
    onError: (error) => {
      console.error("ERROR IN SENDING EMAIL:", error);
    },
  });

  const sendEmail = (data: SponsorshipEmailFields) => {
    emailMutate(data);
  };

  const handleSend = () => {
    const { firstname, email } = getValues();
    if (sponsorCode && email && firstname) {
      sendEmail({
        code: sponsorCode,
        email,
        firstname,
      });
    } else {
      if (!sponsorCode) {
        toast.error(generateSponsorCodeFirst);
        return;
      } else {
        toast.error(sponsorErrorMessage);
      }
    }
  };

  const onSubmit = (data: SponsorshipFormFields) => {
    const { firstname, lastname } = data;
    mutate({ firstname, lastname });
  };

  return (
    <section className={styles.accountSponsorship}>
      <p>{intro}</p>
      {/* eslint-disable-next-line */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <fieldset>
          <legend>{friendInfo}</legend>
          <label htmlFor="firstname">{firstName}</label>
          <input
            {...register("firstname")}
            placeholder={`${firstName}*`}
            size={15}
          />
          <label htmlFor="lastname">{lastName}</label>
          <input
            {...register("lastname")}
            placeholder={`${lastName}*`}
            size={15}
          />
          <MainButton className={styles.sendButton} type="submit">
            {generateCode}
          </MainButton>
        </fieldset>
      </form>
      <div>
        <p>{sponsorCodeText}</p>
        <p className={styles.code}>
          {sponsorCode ?? <span className={styles.default}>*** *** ***</span>}{" "}
        </p>
      </div>
      <p>{sponsorCodeRequest}</p>
      <p>{sendByEmail}</p>
      <label htmlFor="email" className={styles.labelClass}>
        {email}
      </label>
      <input
        {...register("email")}
        placeholder={`${email}*`}
        size={15}
        className={styles.inputClass}
      />
      <MainButton className={styles.sendButton} onClick={handleSend}>
        {send}
      </MainButton>
    </section>
  );
};

export default AccountSponsorship;
